import { useEffect, useState } from 'react';
import type { v1 } from '@userlike/messenger';
import { logger, useConfig } from '@packages/utilities';
import { useRouter } from 'next/router';
import type { ChatBotConfig } from '@packages/config/src/default';

/**
 * Mounts or unmounts the Userlike Messenger (ChatBot) based on the current route.
 */
export const ChatBotInit = ({
  includePaths = [],
  excludePaths = [],
  visibility,
  key,
}: ChatBotConfig) => {
  const [apiInstance, setApiInstance] = useState<v1.Api | null>(null);

  /**
   * Checks the current route to decide if ChatBot should be displayed.
   */
  const { asPath } = useRouter();
  const pathname = Array.isArray(asPath) ? asPath.join('/') : asPath || '';
  const shouldInclude = includePaths.some((includePath) => pathname.includes(includePath));
  const shouldExclude = excludePaths.some((excludePath) => pathname.includes(excludePath));
  const showChatBot = shouldInclude && !shouldExclude;

  /**
   * Asynchronously creates and mounts the ChatBot API.
   */
  const createAndMountApi = async () => {
    const { createMessenger } = await import('@userlike/messenger');

    const result = await createMessenger({
      version: 1,
      widgetKey: key,
    });

    if (result.kind === 'error') throw new Error(result.error);

    const { api } = result.value;
    if (api === null) {
      throw new Error('API reached end-of-life, please check documentation and upgrade.');
    }

    const mountResult = await api.mount();
    if (mountResult.kind === 'error') throw new Error(mountResult.error);

    await api.setVisibility(visibility);

    setApiInstance(api);
  };

  /**
   * Manages ChatBot mounting/unmounting via useEffect.
   * @see createAndMountApi
   */
  useEffect(() => {
    if (showChatBot && apiInstance === null) {
      createAndMountApi().catch((e) => {
        logger.error(e, `Failed to mount ChatBot messenger: ${e}`);
      });
    } else if (!showChatBot && apiInstance?.unmount) {
      apiInstance
        .unmount()
        .then(() => setApiInstance(null))
        .catch((e) => logger.error(e, `Failed to unmount ChatBot messenger: ${e}`));
    }
  }, [showChatBot, apiInstance]);
};

/**
 * Wrapper component for `ChatBotInit`. This is a side-effect-only component,
 * meaning it performs some operation (mounting or unmounting the chatbot)
 * without rendering any UI itself. Therefore, it returns `null`.
 *
 * @see https://github.com/userlike/messenger
 * @see https://docs.userlike.com/setup/integration/api
 * @returns {null}
 */
export const ChatBot = (): null => {
  const { chatBot: chatBotConfig } = useConfig();

  if (chatBotConfig.enabled && chatBotConfig.key !== '') {
    ChatBotInit(chatBotConfig);
  }

  return null;
};

export default ChatBot;
